import React, { useState } from 'react';
import { Box, Card, Stack, Typography, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Chip, IconButton, Tooltip, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import { resolveGroupsAssetUrl } from '../../../api/httpClientGroups';
import {
  fetchGuestsByPersonId,
  updateGuest,
  removeGuest,
  updateGuestAttendance,
  downloadEventAttendanceReportExcel,
  fetchEventAttendanceReport,
} from '../../../controllers/clubEventsController';
import EventAttendanceReportDialog from './dialogs/EventAttendanceReportDialog';

const ParticipantsTable = ({
  participants,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  onAttendanceStatusChange,
  onRemoveParticipant,
  getParticipantTypeLabel,
  eventId,
  allowGuests,
  onGuestUpdated,
  onAddClick,
}) => {
  const [expandedParticipants, setExpandedParticipants] = useState(new Set());
  const [guestsData, setGuestsData] = useState({});
  const [loadingGuests, setLoadingGuests] = useState(new Set());
  const [editGuestDialogOpen, setEditGuestDialogOpen] = useState(false);
  const [deleteGuestDialogOpen, setDeleteGuestDialogOpen] = useState(false);
  const [deleteParticipantDialogOpen, setDeleteParticipantDialogOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [guestForm, setGuestForm] = useState({ firstName: '', lastName: '', phoneNumber: '' });
  const [excelLoading, setExcelLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState(null);

  if (!Array.isArray(participants)) return null;

  const handleToggleExpand = async (participant) => {
    const participantId = participant.id;
    const personId = participant.personId || participant.PersonId;
    const personType = participant.type ?? participant.Type ?? 0;
    
    if (expandedParticipants.has(participantId)) {
      // Collapse
      setExpandedParticipants((prev) => {
        const newSet = new Set(prev);
        newSet.delete(participantId);
        return newSet;
      });
    } else {
      // Expand - Load guests
      setExpandedParticipants((prev) => new Set(prev).add(participantId));
      
      if (!guestsData[participantId] && allowGuests) {
        setLoadingGuests((prev) => new Set(prev).add(participantId));
        try {
          const guests = await fetchGuestsByPersonId(eventId, personId, personType);
          setGuestsData((prev) => ({
            ...prev,
            [participantId]: Array.isArray(guests) ? guests : [],
          }));
        } catch (e) {
          setGuestsData((prev) => ({
            ...prev,
            [participantId]: [],
          }));
        } finally {
          setLoadingGuests((prev) => {
            const newSet = new Set(prev);
            newSet.delete(participantId);
            return newSet;
          });
        }
      }
    }
  };

  const handleEditGuest = (guest, participant) => {
    setSelectedGuest(guest);
    setSelectedParticipant(participant);
    setGuestForm({
      firstName: guest.firstName || '',
      lastName: guest.lastName || '',
      phoneNumber: guest.phoneNumber || '',
    });
    setEditGuestDialogOpen(true);
  };

  const handleUpdateGuest = async () => {
    if (!selectedGuest || !eventId) return;
    try {
      await updateGuest({
        EventId: eventId,
        GuestId: selectedGuest.id,
        FirstName: guestForm.firstName.trim(),
        LastName: guestForm.lastName.trim(),
        PhoneNumber: guestForm.phoneNumber.trim() || null,
      });
      setEditGuestDialogOpen(false);
      setSelectedGuest(null);
      setGuestForm({ firstName: '', lastName: '', phoneNumber: '' });
      // Refresh guests
      if (selectedParticipant) {
        const participantId = selectedParticipant.id;
        const personId = selectedParticipant.personId || selectedParticipant.PersonId;
        const personType = selectedParticipant.type ?? selectedParticipant.Type ?? 0;
        const guests = await fetchGuestsByPersonId(eventId, personId, personType);
        setGuestsData((prev) => ({
          ...prev,
          [participantId]: Array.isArray(guests) ? guests : [],
        }));
      }
      onGuestUpdated?.();
    } catch (e) {
      alert(e?.message || 'Misafir güncellenemedi');
    }
  };

  const handleDeleteGuest = async () => {
    if (!selectedGuest || !eventId) return;
    try {
      await removeGuest({
        EventId: eventId,
        GuestId: selectedGuest.id,
      });
      setDeleteGuestDialogOpen(false);
      setSelectedGuest(null);
      // Refresh guests
      if (selectedParticipant) {
        const participantId = selectedParticipant.id;
        const personId = selectedParticipant.personId || selectedParticipant.PersonId;
        const personType = selectedParticipant.type ?? selectedParticipant.Type ?? 0;
        const guests = await fetchGuestsByPersonId(eventId, personId, personType);
        setGuestsData((prev) => ({
          ...prev,
          [participantId]: Array.isArray(guests) ? guests : [],
        }));
      }
      onGuestUpdated?.();
    } catch (e) {
      alert(e?.message || 'Misafir silinemedi');
    }
  };

  const handleGuestAttendanceChange = async (guest, participant, newStatus) => {
    if (!eventId) return;
    try {
      await updateGuestAttendance({
        EventId: eventId,
        GuestId: guest.id,
        AttendanceStatus: newStatus,
      });
      // Refresh guests
      const participantId = participant.id;
      const personId = participant.personId || participant.PersonId;
      const personType = participant.type ?? participant.Type ?? 0;
      const guests = await fetchGuestsByPersonId(eventId, personId, personType);
      setGuestsData((prev) => ({
        ...prev,
        [participantId]: Array.isArray(guests) ? guests : [],
      }));
      onGuestUpdated?.();
    } catch (e) {
      alert(e?.message || 'Misafir katılım durumu güncellenemedi');
    }
  };

  const getAttendanceLabel = (status) => {
    const v = typeof status === 'number' ? status : undefined;
    switch (v) {
      case 0: return 'Beklemede';
      case 1: return 'Katıldı';
      case 2: return 'Gelmedi';
      case 3: return 'İptal';
      case 4: return 'Sırada Bekliyor';
      default: return String(status ?? '');
    }
  };

  const attendanceOverrideTooltip = 'Üye iptal talebi akışı için İptal Talepleri sayfasını kullanın; bu alan doğrudan admin override içindir.';

  const handleDownloadExcel = async () => {
    if (!eventId || excelLoading) return;
    setExcelLoading(true);
    try {
      const { blob, fileName, contentType } = await downloadEventAttendanceReportExcel(eventId);
      const url = URL.createObjectURL(new Blob([blob], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `etkinlik-katilim-${eventId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Katılım listesi indirildi');
    } catch (e) {
      toast.error(e?.message || 'Katılım listesi indirilemedi');
    } finally {
      setExcelLoading(false);
    }
  };

  const handleViewReport = async () => {
    if (!eventId || reportLoading) return;
    setReportLoading(true);
    try {
      const data = await fetchEventAttendanceReport(eventId);
      setReportData(data);
      setReportDialogOpen(true);
    } catch (e) {
      toast.error(e?.message || 'Rapor yüklenemedi');
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setReportData(null);
  };

  return (
    <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
            Katılımcılar
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="contained"
              size="small"
              disabled={reportLoading || !eventId}
              onClick={handleViewReport}
              startIcon={reportLoading ? <CircularProgress size={16} color="inherit" /> : <VisibilityIcon sx={{ width: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                borderRadius: '5px',
                backgroundColor: '#E7EDF4',
                boxShadow: 'none',
                color: '#0E4D94',
                '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
              }}
            >
              Raporu Görüntüle
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={excelLoading || reportLoading || !eventId}
              onClick={handleDownloadExcel}
              startIcon={excelLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon sx={{ width: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                borderRadius: '5px',
                borderColor: '#e6e6e6',
                color: '#444444',
                '&:hover': {
                  borderColor: '#0E4D94',
                  color: '#0E4D94',
                  backgroundColor: 'rgba(14, 77, 148, 0.04)',
                },
              }}
            >
              Excel İndir
            </Button>
            {onAddClick && (
              <Button
                variant="contained"
                size="small"
                onClick={onAddClick}
                startIcon={<AddIcon sx={{ width: 16 }} />}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#E7EDF4',
                  boxShadow: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  color: '#0E4D94',
                  '&:hover': {
                    backgroundColor: '#CFDBEA',
                    boxShadow: 'none',
                  },
                }}
              >
                Katılımcı Ekle
              </Button>
            )}
          </Stack>
        </Stack>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Ara"
          placeholder="İsim veya telefon"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 220,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '14px',
                '& fieldset': {
                  borderColor: '#e6e6e6',
                },
                '&:hover fieldset': {
                  borderColor: '#0E4D94',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0E4D94',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '14px',
              },
            }}
          />
          <TextField
            size="small"
            select
            label="Durum"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '14px',
                '& fieldset': {
                  borderColor: '#e6e6e6',
                },
                '&:hover fieldset': {
                  borderColor: '#0E4D94',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0E4D94',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '14px',
              },
            }}
          >
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="0">Beklemede</MenuItem>
            <MenuItem value="1">Katıldı</MenuItem>
            <MenuItem value="2">Gelmedi</MenuItem>
            <MenuItem value="3">İptal</MenuItem>
            <MenuItem value="4">Sırada Bekliyor</MenuItem>
          </TextField>
          {statusFilter !== '4' && (
            <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', whiteSpace: 'nowrap' }}>
              Sırada bekleyenlerin sıra numaralarını görmek için filtreyi "Sırada Bekliyor" olarak ayarlayın
            </Typography>
          )}
      </Box>

        <TableContainer
          sx={{
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f5f5f5',
            py: 1,
            px: 2.5,
            borderRadius: '8px',
          }}
        >
        <Table size="small">
            <TableHead sx={{ bgcolor: 'transparent' }}>
            <TableRow>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Foto</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Telefon</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Tür</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kayıt Tarihi</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Onay</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                    Katılım
                    <Tooltip title={attendanceOverrideTooltip}>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: '#a3a3a3', cursor: 'help' }} />
                      </Box>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>{statusFilter === '4' ? 'Sıra Numarası' : 'Katıldığı Zaman'}</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Misafir</TableCell>
                <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
              {participants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                      Henüz katılımcı bulunmamaktadır.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                participants.map((p, index) => {
              const isExpanded = expandedParticipants.has(p.id);
              const guests = guestsData[p.id] || [];
              const isLoadingGuests = loadingGuests.has(p.id);
              const hasGuests = allowGuests && typeof (p.guestCount ?? p.GuestCount) === 'number' && (p.guestCount ?? p.GuestCount) > 0;
              
              return (
                <React.Fragment key={p.id}>
                      <TableRow
                        hover
                        sx={{
                          '& .MuiTableCell-root': {
                            borderBottom: index === participants.length - 1 && !isExpanded ? 'none' : '1px dashed #e8e8e8',
                            px: 1.5,
                            py: 1.5,
                            color: '#444444',
                            fontWeight: 500,
                            fontSize: '14px',
                          },
                          '&:hover': {
                            backgroundColor: '#fafafa',
                          },
                        }}
                      >
                    <TableCell>
                      <Avatar src={resolveGroupsAssetUrl(p.profilePictureUrl || p.ProfilePictureUrl)} sx={{ width: 32, height: 32 }} />
                    </TableCell>
                    <TableCell>{p.fullName}</TableCell>
                    <TableCell>{p.phoneNumber}</TableCell>
                        <TableCell>
                          <Chip
                            label={getParticipantTypeLabel(p.type ?? p.Type)}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '11px',
                              fontWeight: 500,
                              backgroundColor: '#E7EDF4',
                              color: '#0E4D94',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                    <TableCell>{p.registeredAt ? new Date(p.registeredAt).toLocaleString('tr-TR') : ''}</TableCell>
                        <TableCell>
                          {p.isApproved ? (
                            <Chip
                              label="Evet"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: '#DFFFEA',
                                color: '#17C653',
                                border: 'none',
                              }}
                            />
                          ) : (
                            <Chip
                              label="Hayır"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: '#FFEEF3',
                                color: '#F8285A',
                                border: 'none',
                              }}
                            />
                          )}
                        </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        select
                        value={typeof (p.attendanceStatus ?? p.AttendanceStatus) === 'number' ? (p.attendanceStatus ?? p.AttendanceStatus) : ''}
                        onChange={(e) => onAttendanceStatusChange(p, Number(e.target.value))}
                        sx={{
                          minWidth: 140,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '14px',
                            '& fieldset': {
                              borderColor: '#e6e6e6',
                            },
                            '&:hover fieldset': {
                              borderColor: '#0E4D94',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#0E4D94',
                            },
                          },
                        }}
                      >
                        <MenuItem value={0}>Beklemede</MenuItem>
                        <MenuItem value={1}>Katıldı</MenuItem>
                        <MenuItem value={2}>Gelmedi</MenuItem>
                        <MenuItem value={3}>İptal</MenuItem>
                        <MenuItem value={4}>Sırada Bekliyor</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {statusFilter === '4' ? (
                        typeof (p.queueNumber ?? p.QueueNumber) === 'number' ? (
                              <Chip
                                label={`#${p.queueNumber ?? p.QueueNumber}`}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  backgroundColor: '#E7EDF4',
                                  color: '#0E4D94',
                                  border: 'none',
                                }}
                              />
                        ) : (
                          '-'
                        )
                      ) : (
                        p.attendedAt ? new Date(p.attendedAt).toLocaleString('tr-TR') : ''
                      )}
                    </TableCell>
                    <TableCell>
                      {hasGuests ? (
                            <Chip
                              label={`${p.guestCount ?? p.GuestCount}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: '#FFF4E6',
                                color: '#FF9800',
                                border: 'none',
                              }}
                            />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        {hasGuests && (
                          <Tooltip title={isExpanded ? "Misafirleri Gizle" : "Misafirleri Göster"}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleExpand(p)}
                              sx={{
                                width: 28,
                                height: 28,
                                backgroundColor: '#E7EDF4',
                                color: '#0E4D94',
                                '&:hover': {
                                  backgroundColor: '#CFDBEA',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                },
                              }}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                      <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setParticipantToDelete(p);
                              setDeleteParticipantDialogOpen(true);
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: '#FFEEF3',
                              color: '#F8285A',
                              '&:hover': {
                                backgroundColor: '#FFD6E0',
                              },
                              '& svg': {
                                fontSize: '16px',
                              },
                            }}
                          >
                            <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {isExpanded && hasGuests && (
                    <TableRow>
                          <TableCell
                            colSpan={10}
                            sx={{
                              py: 2,
                              bgcolor: '#fafafa',
                              borderBottom: index === participants.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            }}
                          >
                        <Box sx={{ pl: 4 }}>
                          {isLoadingGuests ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : guests.length === 0 ? (
                                <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666' }}>
                              Bu katılımcının misafiri bulunmamaktadır.
                            </Typography>
                          ) : (
                                <Stack spacing={1.5}>
                                  <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                                Misafirler ({guests.length})
                              </Typography>
                              <Table size="small">
                                    <TableHead sx={{ bgcolor: 'transparent' }}>
                                  <TableRow>
                                        <TableCell sx={{ fontSize: '13px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                                        <TableCell sx={{ fontSize: '13px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Telefon</TableCell>
                                        <TableCell sx={{ fontSize: '13px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kayıt Tarihi</TableCell>
                                        <TableCell sx={{ fontSize: '13px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Katılım</TableCell>
                                        <TableCell sx={{ fontSize: '13px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Katıldığı Zaman</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                      {guests.map((guest, guestIndex) => (
                                        <TableRow
                                          key={guest.id}
                                          sx={{
                                            '& .MuiTableCell-root': {
                                              borderBottom: guestIndex === guests.length - 1 ? 'none' : '1px dashed #e8e8e8',
                                              px: 1.5,
                                              py: 1.5,
                                              color: '#444444',
                                              fontWeight: 500,
                                              fontSize: '14px',
                                            },
                                            '&:hover': {
                                              backgroundColor: '#ffffff',
                                            },
                                          }}
                                        >
                                      <TableCell>{guest.fullName || `${guest.firstName} ${guest.lastName}`}</TableCell>
                                      <TableCell>{guest.phoneNumber || '-'}</TableCell>
                                      <TableCell>
                                        {guest.registeredAt ? new Date(guest.registeredAt).toLocaleString('tr-TR') : '-'}
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          size="small"
                                          select
                                          value={typeof guest.attendanceStatus === 'number' ? guest.attendanceStatus : ''}
                                          onChange={(e) => handleGuestAttendanceChange(guest, p, Number(e.target.value))}
                                              sx={{
                                                minWidth: 140,
                                                '& .MuiOutlinedInput-root': {
                                                  borderRadius: '8px',
                                                  fontSize: '14px',
                                                  '& fieldset': {
                                                    borderColor: '#e6e6e6',
                                                  },
                                                  '&:hover fieldset': {
                                                    borderColor: '#0E4D94',
                                                  },
                                                  '&.Mui-focused fieldset': {
                                                    borderColor: '#0E4D94',
                                                  },
                                                },
                                              }}
                                        >
                                          <MenuItem value={0}>Beklemede</MenuItem>
                                          <MenuItem value={1}>Katıldı</MenuItem>
                                          <MenuItem value={2}>Gelmedi</MenuItem>
                                          <MenuItem value={3}>İptal</MenuItem>
                                          <MenuItem value={4}>Sırada Bekliyor</MenuItem>
                                        </TextField>
                                      </TableCell>
                                      <TableCell>
                                        {guest.attendedAt ? new Date(guest.attendedAt).toLocaleString('tr-TR') : '-'}
                                      </TableCell>
                                      <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                        <Tooltip title="Düzenle">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditGuest(guest, p)}
                                                  sx={{
                                                    width: 28,
                                                    height: 28,
                                                    backgroundColor: '#E7EDF4',
                                                    color: '#0E4D94',
                                                    '&:hover': {
                                                      backgroundColor: '#CFDBEA',
                                                    },
                                                    '& svg': {
                                                      fontSize: '16px',
                                                    },
                                                  }}
                                          >
                                                  <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Sil">
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setSelectedGuest(guest);
                                              setSelectedParticipant(p);
                                              setDeleteGuestDialogOpen(true);
                                            }}
                                                  sx={{
                                                    width: 28,
                                                    height: 28,
                                                    backgroundColor: '#FFEEF3',
                                                    color: '#F8285A',
                                                    '&:hover': {
                                                      backgroundColor: '#FFD6E0',
                                                    },
                                                    '& svg': {
                                                      fontSize: '16px',
                                                    },
                                                  }}
                                          >
                                                  <DeleteOutlineIcon />
                                          </IconButton>
                                        </Tooltip>
                                            </Stack>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Stack>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
                })
              )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Guest Dialog */}
        <Dialog
          open={editGuestDialogOpen}
          onClose={() => setEditGuestDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '8px',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2.5,
              pb: 2,
              borderBottom: '1px solid #e6e6e6',
            }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444' }}>
              Misafir Düzenle
            </Typography>
            <IconButton
              onClick={() => setEditGuestDialogOpen(false)}
              sx={{
                width: 32,
                height: 32,
                color: '#666666',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2.5 }}>
            <Stack spacing={2}>
            <TextField
              label="Ad"
              fullWidth
              required
              value={guestForm.firstName}
              onChange={(e) => setGuestForm((f) => ({ ...f, firstName: e.target.value }))}
              inputProps={{ maxLength: 100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '14px',
                    '& fieldset': {
                      borderColor: '#e6e6e6',
                    },
                    '&:hover fieldset': {
                      borderColor: '#0E4D94',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                  },
                }}
            />
            <TextField
              label="Soyad"
              fullWidth
              required
              value={guestForm.lastName}
              onChange={(e) => setGuestForm((f) => ({ ...f, lastName: e.target.value }))}
              inputProps={{ maxLength: 100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '14px',
                    '& fieldset': {
                      borderColor: '#e6e6e6',
                    },
                    '&:hover fieldset': {
                      borderColor: '#0E4D94',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                  },
                }}
            />
            <TextField
              label="Telefon (Opsiyonel)"
              fullWidth
              value={guestForm.phoneNumber}
              onChange={(e) => setGuestForm((f) => ({ ...f, phoneNumber: e.target.value }))}
              inputProps={{ maxLength: 20 }}
              placeholder="+90..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '14px',
                    '& fieldset': {
                      borderColor: '#e6e6e6',
                    },
                    '&:hover fieldset': {
                      borderColor: '#0E4D94',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                  },
                }}
            />
          </Stack>
        </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2, borderTop: '1px solid #e6e6e6' }}>
            <Button
              onClick={() => setEditGuestDialogOpen(false)}
              sx={{
                px: 2,
                py: 1,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#666666',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              İptal
            </Button>
          <Button
            variant="contained"
            onClick={handleUpdateGuest}
            disabled={!guestForm.firstName.trim() || !guestForm.lastName.trim()}
              sx={{
                px: 2,
                py: 1,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                backgroundColor: '#0E4D94',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#0a3d75',
                },
                '&:disabled': {
                  backgroundColor: '#e6e6e6',
                  color: '#a3a3a3',
                },
              }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

        {/* Delete Participant Dialog */}
        <Dialog
          open={deleteParticipantDialogOpen}
          onClose={() => {
            setDeleteParticipantDialogOpen(false);
            setParticipantToDelete(null);
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '18px',
              fontWeight: 500,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pr: 3,
            }}
          >
            Katılımcı Sil
            <IconButton
              onClick={() => {
                setDeleteParticipantDialogOpen(false);
                setParticipantToDelete(null);
              }}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2, px: 3 }}>
            <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
              {participantToDelete
                ? `"${participantToDelete.fullName}" katılımcısını silmek istediğinize emin misiniz?`
                : 'Bu katılımcıyı silmek istediğinize emin misiniz?'}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ py: 2, px: 3 }}>
            <Button
              onClick={() => {
                setDeleteParticipantDialogOpen(false);
                setParticipantToDelete(null);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                color: '#666666',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (participantToDelete) {
                  onRemoveParticipant(participantToDelete);
                }
                setDeleteParticipantDialogOpen(false);
                setParticipantToDelete(null);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                backgroundColor: '#F8285A',
                '&:hover': {
                  backgroundColor: '#d91e47',
                },
              }}
            >
              Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Guest Dialog */}
        <Dialog
          open={deleteGuestDialogOpen}
          onClose={() => setDeleteGuestDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '8px',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2.5,
              pb: 2,
              borderBottom: '1px solid #e6e6e6',
            }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444' }}>
              Misafir Sil
            </Typography>
            <IconButton
              onClick={() => setDeleteGuestDialogOpen(false)}
              sx={{
                width: 32,
                height: 32,
                color: '#666666',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2.5 }}>
            <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            {selectedGuest
              ? `"${selectedGuest.fullName || `${selectedGuest.firstName} ${selectedGuest.lastName}`}" misafirini silmek istediğinize emin misiniz?`
              : 'Bu misafiri silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2, borderTop: '1px solid #e6e6e6' }}>
            <Button
              onClick={() => setDeleteGuestDialogOpen(false)}
              sx={{
                px: 2,
                py: 1,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#666666',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteGuest}
              sx={{
                px: 2,
                py: 1,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                backgroundColor: '#F8285A',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d91e47',
                },
              }}
            >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <EventAttendanceReportDialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        data={reportData}
      />
      </Stack>
    </Card>
  );
};

export default ParticipantsTable;

